import "../candidate-intelligence/results/styles.css";
import type { LevelAssessmentResult } from "@shared/labs/level-assessment";
import type { LevelAssessmentDebug } from "@shared/labs/level-assessment-debug";
import { ChevronLeft } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "wouter";
import { PageHeader } from "@/components/page-header";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { ActiveModelProvider, useActiveModel } from "./active-model-context";
import { type DemoKey, getDemoFixture } from "./demo-data";
import { FormView, type InputMode } from "./form-view";
import { ModelPicker } from "./model-picker";
import { ProcessingView, type StreamProgress } from "./processing-view";
import { LevelAssessmentReport } from "./results";
import type { ExtractedProfileViewModel } from "./results/extracted-profile-panel";
import type { ReviewContext } from "./results/review-panel";

type View = "form" | "processing" | "results";

const INITIAL_PROGRESS: StreamProgress = {
  profileReady: false,
  axesReady: false,
};

/**
 * Read-only demo shortcut: `?demo=<key>` jumps straight to a static fixture.
 * Helpful for design review and Playwright baselines while the backend
 * (Phase 2 of the plan) is still being built.
 */
function readDemoParam(): DemoKey | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("demo");
  if (
    v === "junior-cross" ||
    v === "senior-cross" ||
    v === "abstention" ||
    v === "eng-discipline"
  ) {
    return v;
  }
  return null;
}

export default function LabsLevelAssessment() {
  const demo = readDemoParam();
  if (demo) return <DemoReport demoKey={demo} />;
  return (
    <ActiveModelProvider>
      <LevelAssessmentInner />
    </ActiveModelProvider>
  );
}

function DemoReport({ demoKey }: { demoKey: DemoKey }) {
  const fixture = getDemoFixture(demoKey);
  return (
    <>
      <PageHeaderRow />
      <LevelAssessmentReport
        candidateName={fixture.candidateName}
        contextLabel={fixture.contextLabel}
        result={fixture.result}
        extractedProfile={fixture.extractedProfile}
        onReset={() => {
          const url = new URL(window.location.href);
          url.searchParams.delete("demo");
          window.location.href = url.toString();
        }}
      />
    </>
  );
}

function PageHeaderRow() {
  return (
    <div>
      <Link
        href="/labs/level-assessment/candidates"
        data-testid="evaluate-back-to-candidates"
        className="mb-2 inline-flex items-center gap-1.5"
        style={{ fontSize: 13, color: "var(--ink-muted)", textDecoration: "none" }}
      >
        <ChevronLeft size={15} /> Candidatos
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p
            className="mb-1 font-semibold uppercase"
            style={{
              fontSize: "11px",
              letterSpacing: 0,
              lineHeight: 1.4,
              color: "var(--ink-muted)",
            }}
          >
            Alpha · Labs · Avaliação avulsa
          </p>
          <PageHeader
            title="Avaliação de Nível"
            description="Inferência universal de senioridade (L0–L7) a partir de histórico profissional. Funciona com qualquer função — não requer framework dedicado por disciplina."
          />
        </div>
      </div>
    </div>
  );
}

function LevelAssessmentInner() {
  const { toast } = useToast();
  const { modelId, model, disciplineId, setDisciplineId } = useActiveModel();
  const fileInputRef = useRef<HTMLInputElement>(null!);

  const [view, setView] = useState<View>("form");
  const [inputMode, setInputMode] = useState<InputMode>("url");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [freeText, setFreeText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [streamProgress, setStreamProgress] = useState<StreamProgress>(INITIAL_PROGRESS);
  const [resultReady, setResultReady] = useState(false);
  const [resultPayload, setResultPayload] = useState<{
    candidateName: string;
    candidateSubtitle?: string;
    contextLabel: string;
    result: LevelAssessmentResult;
    extractedProfile?: ExtractedProfileViewModel;
    evaluationId?: string;
    linkedinUrl?: string | null;
    debug?: LevelAssessmentDebug | null;
    reviewContext?: ReviewContext;
  } | null>(null);
  // Read `?debug=1` once at mount. Used as default for the form toggle
  // and forwarded to the backend as the `debug` flag on submit.
  const [debugMode, setDebugMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("debug") === "1";
  });

  const cancelRef = useRef<{ cancelled: boolean } | null>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast({
        title: "Arquivo inválido",
        description: "Envie o export do LinkedIn em formato PDF.",
        variant: "destructive",
      });
      return;
    }
    setPdfFile(file);
  }

  /**
   * Real pipeline — calls the backend usecase via oRPC. The processing view
   * shows two coarse beats because the current endpoint is non-streaming;
   * we mark "profile ready" once the RPC request is in flight and "axes
   * ready" when the response lands. A streaming variant can refine this
   * later without changing the UI contract.
   */
  async function runPipeline() {
    const token = { cancelled: false };
    cancelRef.current = token;

    setSubmitting(true);
    setStreamProgress(INITIAL_PROGRESS);
    setResultReady(false);
    setResultPayload(null);
    setView("processing");

    // Optimistic first beat — by the time the RPC starts, we've already
    // accepted the user input and resolved the actor on the client side.
    setStreamProgress((p) => ({ ...p, profileReady: true }));

    // Runs read the model's persisted signal matrix (the single source of
    // truth). Matrix edits are applied via "Salvar matriz" in the config UI,
    // not threaded per-run from browser-local state — that local path used to
    // drift from the globally-persisted prompts.
    try {
      const response = await rpcClient.labs.levelAssessmentRun({
        modelId,
        disciplineId: disciplineId ?? undefined,
        linkedinUrl: inputMode === "url" ? linkedinUrl : undefined,
        freeText: inputMode === "text" ? freeText : undefined,
        debug: debugMode || undefined,
      });
      if (token.cancelled) return;
      setStreamProgress({ profileReady: true, axesReady: true });
      setResultPayload({
        candidateName: response.candidateName,
        candidateSubtitle: response.candidateSubtitle,
        contextLabel: response.contextLabel,
        result: response.result,
        extractedProfile: response.extractedProfile,
        evaluationId: response.evaluationId,
        linkedinUrl: inputMode === "url" ? linkedinUrl.trim() : null,
        debug: response.debug ?? null,
        // Review (calibration sample collection) is only offered for
        // LinkedIn-sourced runs. It prefers the persisted run's snapshot
        // (evaluationId) and falls back to re-fetching the URL.
        reviewContext:
          inputMode === "url" && linkedinUrl.trim().length > 0
            ? {
                modelId,
                disciplineId: disciplineId ?? null,
                evaluationId: response.evaluationId,
                linkedinUrl: linkedinUrl.trim(),
                candidateName: response.candidateName ?? null,
              }
            : undefined,
      });
      setResultReady(true);
    } catch (err) {
      if (token.cancelled) return;
      const message = err instanceof Error ? err.message : "Erro inesperado";
      toast({
        title: "Falha na avaliação",
        description: message,
        variant: "destructive",
      });
      setView("form");
    } finally {
      setSubmitting(false);
    }
  }

  function loadDemoIntoResults(key: DemoKey) {
    const fixture = getDemoFixture(key);
    setResultPayload(fixture);
    setResultReady(true);
    setStreamProgress({ profileReady: true, axesReady: true });
    setView("results");
  }

  function reset() {
    cancelRef.current = { cancelled: true };
    setView("form");
    setStreamProgress(INITIAL_PROGRESS);
    setResultReady(false);
    setResultPayload(null);
    setSubmitting(false);
  }

  return (
    <>
      <PageHeaderRow />

      {view === "form" && (
        <>
          <ModelPicker />
          <FormView
            inputMode={inputMode}
            setInputMode={setInputMode}
            linkedinUrl={linkedinUrl}
            setLinkedinUrl={setLinkedinUrl}
            pdfFile={pdfFile}
            setPdfFile={setPdfFile}
            freeText={freeText}
            setFreeText={setFreeText}
            disciplines={(model?.disciplines ?? []).map((d) => ({ id: d.id, label: d.label }))}
            disciplineId={disciplineId}
            setDisciplineId={setDisciplineId}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            onSubmit={runPipeline}
            onLoadDemo={loadDemoIntoResults}
            submitting={submitting}
            debugMode={debugMode}
            setDebugMode={setDebugMode}
          />
        </>
      )}

      {view === "processing" && (
        <ProcessingView
          streamProgress={streamProgress}
          resultReady={resultReady}
          onShowResults={() => setView("results")}
          onCancel={reset}
        />
      )}

      {view === "results" && resultPayload && (
        <LevelAssessmentReport
          candidateName={resultPayload.candidateName}
          candidateSubtitle={resultPayload.candidateSubtitle}
          contextLabel={resultPayload.contextLabel}
          result={resultPayload.result}
          extractedProfile={resultPayload.extractedProfile}
          evaluationId={resultPayload.evaluationId}
          linkedinUrl={resultPayload.linkedinUrl}
          debug={resultPayload.debug}
          reviewContext={resultPayload.reviewContext}
          onReset={reset}
        />
      )}
    </>
  );
}
